import { getStoredPermissions, storedSessionHasPermission } from "./secureStorageSession";
import {
    DASHLY_PERMISSIONS,
    SETTINGS_TRANSLATION_EDIT_PERMISSIONS,
    SETTINGS_TRANSLATION_GLOBAL_EDIT_PERMISSIONS,
    SETTINGS_TRANSLATION_GLOBAL_VIEW_PERMISSIONS,
    SETTINGS_TRANSLATION_VIEW_PERMISSIONS,
} from "./dashlyPermissions";
import { PROCESSFLOW_TYPE_OBJECTIVE, ProcessflowRouteType } from "../Pages/Processflow/processflowConstants";

export const hasPermission = (permissions: string[], permissionName: string): boolean =>
    storedSessionHasPermission(permissions, permissionName);

/** Instance-level resource access; aligns with backend {@code RequiredPermissionService#userHasRequiredPermission}. */
export const hasRequiredResourcePermission = (
    permissions: string[],
    requiredPermission?: string | null,
): boolean => {
    const required = requiredPermission?.trim();
    if (!required) {
        return true;
    }
    return permissions.some((granted) => granted === required);
};

export const hasAnyPermission = (permissions: string[], ...permissionNames: string[]): boolean =>
    permissionNames.some((permissionName) => hasPermission(permissions, permissionName));

export interface CrudAccess {
    permissions: string[];
    canView: boolean;
    canAdd: boolean;
    canEdit: boolean;
    canDelete: boolean;
}

export const readCrudAccess = (
    viewPermission: string,
    addPermission: string,
    editPermission: string,
    deletePermission: string,
): CrudAccess => {
    const permissions = getStoredPermissions();
    return {
        permissions,
        canView: hasPermission(permissions, viewPermission),
        canAdd: hasPermission(permissions, addPermission),
        canEdit: hasPermission(permissions, editPermission),
        canDelete: hasPermission(permissions, deletePermission),
    };
};

export const readAlertAccess = () =>
    readCrudAccess(
        DASHLY_PERMISSIONS.ALERT_VIEW,
        DASHLY_PERMISSIONS.ALERT_ADD,
        DASHLY_PERMISSIONS.ALERT_EDIT,
        DASHLY_PERMISSIONS.ALERT_DELETE,
    );

export interface ProjectAccess extends CrudAccess {
    canApproveCustomer: boolean;
    canApproveDashly: boolean;
    canAdvancePhase: boolean;
    canEditStakeholders: boolean;
    canViewSupport: boolean;
    canExportJson: boolean;
    canCreateLegacy: boolean;
    canEditLegacy: boolean;
    canCreateUser: boolean;
}

export const readProjectAccess = (): ProjectAccess => {
    const crud = readCrudAccess(
        DASHLY_PERMISSIONS.PROJECT_VIEW,
        DASHLY_PERMISSIONS.PROJECT_ADD,
        DASHLY_PERMISSIONS.PROJECT_EDIT,
        DASHLY_PERMISSIONS.PROJECT_DELETE,
    );
    return {
        ...crud,
        canApproveCustomer: hasPermission(crud.permissions, DASHLY_PERMISSIONS.PROJECT_APPROVE_CUSTOMER),
        canApproveDashly: hasPermission(crud.permissions, DASHLY_PERMISSIONS.PROJECT_APPROVE_DASHLY),
        canAdvancePhase: hasPermission(crud.permissions, DASHLY_PERMISSIONS.PROJECT_PHASE_ADVANCE),
        canEditStakeholders: hasPermission(crud.permissions, DASHLY_PERMISSIONS.PROJECT_STAKEHOLDER_EDIT),
        canViewSupport: hasPermission(crud.permissions, DASHLY_PERMISSIONS.PROJECT_SUPPORT_VIEW),
        canExportJson: hasPermission(crud.permissions, DASHLY_PERMISSIONS.PROJECT_EXPORT_JSON),
        canCreateLegacy: hasPermission(crud.permissions, DASHLY_PERMISSIONS.PROJECT_CREATE_LEGACY),
        canEditLegacy: hasPermission(crud.permissions, DASHLY_PERMISSIONS.PROJECT_EDIT_LEGACY),
        canCreateUser: hasPermission(crud.permissions, "User.Add"),
    };
};

export const canViewProjects = (permissions: string[]): boolean =>
    hasPermission(permissions, DASHLY_PERMISSIONS.PROJECT_VIEW);

export const canApproveProjectAsCustomer = (permissions: string[]): boolean =>
    hasPermission(permissions, DASHLY_PERMISSIONS.PROJECT_APPROVE_CUSTOMER);

export const canApproveProjectAsDashly = (permissions: string[]): boolean =>
    hasPermission(permissions, DASHLY_PERMISSIONS.PROJECT_APPROVE_DASHLY);

export const canAdvanceProjectPhase = (permissions: string[]): boolean =>
    hasPermission(permissions, DASHLY_PERMISSIONS.PROJECT_PHASE_ADVANCE);

export const canViewAlerts = (permissions: string[]): boolean =>
    hasPermission(permissions, DASHLY_PERMISSIONS.ALERT_VIEW);

const crudPermissionsForProcessflowType = (processflowType: ProcessflowRouteType) => {
    if (processflowType === PROCESSFLOW_TYPE_OBJECTIVE) {
        return {
            view: DASHLY_PERMISSIONS.OBJECTIVE_VIEW,
            add: DASHLY_PERMISSIONS.OBJECTIVE_ADD,
            edit: DASHLY_PERMISSIONS.OBJECTIVE_EDIT,
            delete: DASHLY_PERMISSIONS.OBJECTIVE_DELETE,
        };
    }
    return {
        view: DASHLY_PERMISSIONS.PROCESSFLOW_VIEW,
        add: DASHLY_PERMISSIONS.PROCESSFLOW_ADD,
        edit: DASHLY_PERMISSIONS.PROCESSFLOW_EDIT,
        delete: DASHLY_PERMISSIONS.PROCESSFLOW_DELETE,
    };
};

export const canViewProcessflows = (permissions: string[], processflowType: ProcessflowRouteType): boolean =>
    hasPermission(permissions, crudPermissionsForProcessflowType(processflowType).view);

export const canAddProcessflows = (permissions: string[], processflowType: ProcessflowRouteType): boolean =>
    hasPermission(permissions, crudPermissionsForProcessflowType(processflowType).add);

export const canEditProcessflows = (permissions: string[], processflowType: ProcessflowRouteType): boolean =>
    hasPermission(permissions, crudPermissionsForProcessflowType(processflowType).edit);

export const canDeleteProcessflows = (permissions: string[], processflowType: ProcessflowRouteType): boolean =>
    hasPermission(permissions, crudPermissionsForProcessflowType(processflowType).delete);

export const readProcessflowAccess = (processflowType: ProcessflowRouteType) => {
    const permissionSet = crudPermissionsForProcessflowType(processflowType);
    return readCrudAccess(permissionSet.view, permissionSet.add, permissionSet.edit, permissionSet.delete);
};

export const readObjectiveAccess = () => readProcessflowAccess(PROCESSFLOW_TYPE_OBJECTIVE);

export const readEvaluationCriteriaAccess = () => {
    const permissions = getStoredPermissions();
    return {
        permissions,
        canViewExecutionQuery: hasPermission(permissions, DASHLY_PERMISSIONS.EVALUATION_CRITERIA_VIEW_EXECUTION_QUERY),
    };
};

export const readAskAiAccess = () => {
    const permissions = getStoredPermissions();
    return {
        permissions,
        canViewDebugInfo: hasPermission(permissions, DASHLY_PERMISSIONS.ASK_AI_VIEW_DEBUG_INFO),
    };
};

export const readDataHeroAccess = () => {
    const permissions = getStoredPermissions();
    return {
        permissions,
        canView: hasPermission(permissions, DASHLY_PERMISSIONS.DATA_HERO_VIEW),
        canViewRawData: hasPermission(permissions, DASHLY_PERMISSIONS.DATA_HERO_RAW_DATA),
    };
};

export const canViewDataHero = (permissions: string[]): boolean =>
    hasPermission(permissions, DASHLY_PERMISSIONS.DATA_HERO_VIEW);

export const canViewKnowledgeBase = (permissions: string[]): boolean =>
    hasAnyPermission(permissions, DASHLY_PERMISSIONS.KNOWLEDGE_BASE_COMMENT, DASHLY_PERMISSIONS.KNOWLEDGE_BASE_MANAGE);

export const canViewSettingsTranslations = (permissions: string[]): boolean =>
    hasAnyPermission(permissions, ...SETTINGS_TRANSLATION_VIEW_PERMISSIONS);

export const canViewGlobalSettingsTranslations = (permissions: string[]): boolean =>
    hasAnyPermission(permissions, ...SETTINGS_TRANSLATION_GLOBAL_VIEW_PERMISSIONS);

export const canEditSettingsTranslations = (permissions: string[]): boolean =>
    hasAnyPermission(permissions, ...SETTINGS_TRANSLATION_EDIT_PERMISSIONS);

export const canEditGlobalSettingsTranslations = (permissions: string[]): boolean =>
    hasAnyPermission(permissions, ...SETTINGS_TRANSLATION_GLOBAL_EDIT_PERMISSIONS);

export const canAddLanguageInSettingsTranslations = (permissions: string[], globalScope: boolean): boolean =>
    globalScope
        ? canEditGlobalSettingsTranslations(permissions)
        : hasPermission(permissions, DASHLY_PERMISSIONS.SETTINGS_TRANSLATION_ADD_LANGUAGE) ||
          canEditSettingsTranslations(permissions);

export const canAddTagInSettingsTranslations = (permissions: string[], globalScope: boolean): boolean =>
    globalScope
        ? canEditGlobalSettingsTranslations(permissions)
        : hasPermission(permissions, DASHLY_PERMISSIONS.SETTINGS_TRANSLATION_ADD_TAG) ||
          canEditSettingsTranslations(permissions);

export const canDeleteTagInSettingsTranslations = (permissions: string[], globalScope: boolean): boolean =>
    globalScope
        ? canEditGlobalSettingsTranslations(permissions) ||
          hasPermission(permissions, DASHLY_PERMISSIONS.SETTINGS_TRANSLATION_DELETE_TAG)
        : hasPermission(permissions, DASHLY_PERMISSIONS.SETTINGS_TRANSLATION_DELETE_TAG);

export const canDeleteLanguageInSettingsTranslations = (permissions: string[], globalScope: boolean): boolean =>
    globalScope
        ? canEditGlobalSettingsTranslations(permissions) ||
          hasPermission(permissions, DASHLY_PERMISSIONS.SETTINGS_TRANSLATION_DELETE_LANGUAGE)
        : hasPermission(permissions, DASHLY_PERMISSIONS.SETTINGS_TRANSLATION_DELETE_LANGUAGE);

export const readSettingsTranslationAccess = () => {
    const permissions = getStoredPermissions();
    return {
        permissions,
        canView: canViewSettingsTranslations(permissions),
        canViewGlobal: canViewGlobalSettingsTranslations(permissions),
        canEdit: canEditSettingsTranslations(permissions),
        canEditGlobal: canEditGlobalSettingsTranslations(permissions),
        canDeleteTag: (globalScope: boolean) => canDeleteTagInSettingsTranslations(permissions, globalScope),
        canDeleteLanguage: (globalScope: boolean) =>
            canDeleteLanguageInSettingsTranslations(permissions, globalScope),
    };
};

export const canUseSettingsConfidentialShare = (permissions: string[]): boolean =>
    hasPermission(permissions, DASHLY_PERMISSIONS.SETTINGS_CONFIDENTIAL_SHARE_USE);

export const readSettingsConfidentialShareAccess = () => {
    const permissions = getStoredPermissions();
    return {
        permissions,
        canUse: canUseSettingsConfidentialShare(permissions),
    };
};

export const canViewSettingsPlatform = (permissions: string[]): boolean =>
    hasPermission(permissions, DASHLY_PERMISSIONS.SETTINGS_PLATFORM_VIEW);

export const readSettingsPlatformAccess = () => {
    const permissions = getStoredPermissions();
    return {
        permissions,
        canView: canViewSettingsPlatform(permissions),
    };
};
